/* global Stripe */
import { Controller } from '@hotwired/stimulus'
import { put, post } from '../lib/request'
import { on } from '../lib/events'
import { AddressFixer } from '../lib/address_fixer'

const ORDERS_FLASH_URL = '/orders_flash'

// Manages the Stripe Elements checkout flow including email (Link Authentication),
// shipping address, shipping method selection, and payment (Card/Apple Pay/Google Pay).
// Coordinates validation across all fields and sends order updates to the server
// via turbo-stream as the customer fills in their details.
export default class extends Controller {
  static targets = [
    'address',
    'email',
    'shipping',
    'invalidShipping',
    'payment',
    'purchaseButton',
  ]

  static values = {
    apiKey: String,
    clientSecret: String,
    googleMapsApiKey: String,
    updateUrl: String,
    stripeRedirectUrl: String,
    currentAddress: { type: Object, default: {} },
    params: { type: Object, default: {} },
    emailValid: { type: Boolean, default: false },
    addressValid: { type: Boolean, default: false },
    email: String,
    valid: Boolean,
    provider: { type: String, default: 'stripe' },
  }

  // Mounts the payment element when a valid shipping option appears,
  // and listens for shipping method changes to recalculate totals.
  shippingTargetConnected = (element) => {
    if (!this.paymentElementMounted) {
      this.stripeElements.update({ clientSecret: this.clientSecretValue })
      this.paymentElement.mount(this.paymentTarget)
      this.paymentElement.on('change', this.handlePaymentChange)
      this.paymentElementMounted = true
    }

    element.addEventListener('change', () => {
      this.paramsValue.shipping_method = element.value
      this.addCartTotalsSkeleton()
      this.updateOrder()
    })
  }

  // Unmounts the payment element when shipping becomes invalid (e.g. unsupported address).
  invalidShippingTargetConnected = () => {
    if (this.paymentElementMounted) {
      this.paymentElement.unmount()
      this.paymentElementMounted = false
    }
  }

  // Mounts the Stripe Address Element and attaches the change listener on first
  // focus rather than immediately — mounting with defaultValues makes Stripe fire
  // a synthetic change event, which would otherwise hit the update endpoint before
  // the customer has touched anything.
  addressTargetConnected = (element) => {
    this.addressElement.mount(element)

    this.addressElement.on('focus', () => {
      if (!this.emailValidValue) {
        const params = { error: 'Valid email, and name required.' }
        this.updateFlash(params)
      }

      if (!this.addressChangeEvent) {
        this.addressChangeEvent = true
        this.addressElement.on('change', this.handleAddressChange)
      }
    })
  }

  // Mounts the Stripe Link Authentication Element and listens for email changes.
  emailTargetConnected = (element) => {
    this.emailElement.mount(element)

    this.emailElement.on('ready', () => {
      this.emailElement.on('change', this.handleEmailChange)
    })
  }

  // Creates the Stripe instance and all three Elements (address, email, payment).
  // Subscribes to the paypalEnabled event to unmount Stripe elements when
  // PayPal is selected (prevents background address updates on mobile Safari).
  initialize() {
    this.paymentElementMounted = false
    this.validValue = this.emailValidValue && this.addressValidValue
    this.stripe = Stripe(this.apiKeyValue)
    this.stripeElements = this.stripe.elements({ clientSecret: this.clientSecretValue }, this.appearance)
    this.addressElement = this.stripeElements.create('address', this.addressOptions)
    this.emailElement = this.stripeElements.create('linkAuthentication', this.emailOptions)
    this.paymentElement = this.stripeElements.create('payment', this.paymentOptions)
    this.throttler = undefined

    on('paypalEnabled', this.unmountElements)
  }

  // Runs initial validation check on controller connect.
  connect = () => {
    this.checkValid()
  }

  // Updates email param and triggers address value fetch when email is valid,
  // so the address element's current state is captured. Always runs checkValid
  // so email completing after the address already has (or vice versa) still
  // triggers an update rather than requiring both to complete in one event.
  handleEmailChange = (event) => {
    this.emailValidValue = event.complete
    this.paramsValue.email = event.value.email

    if (this.emailValidValue) {
      this.addressElement.getValue()
    }
    this.checkValid()
  }

  // Processes address changes from the Stripe change event. Always runs
  // checkValid (which has its own validValue guard before sending anything) -
  // gating this on event.complete meant whichever field became valid last
  // needed to be the one whose own handler ran checkValid, so an update could
  // be silently skipped depending on fill order.
  handleAddressChange = (event) => {
    this.addressValidValue = event.complete
    this.updateParams(event)
    this.addressElement.getValue()
    this.checkValid()
  }

  // Toggles the purchase button based on payment readiness.
  // For card: active when CC details are complete.
  // For Apple Pay/Google Pay: active when the wallet is selected
  // (the wallet dialog handles the actual transaction).
  // Binds the click handler once to prevent double payment submissions.
  handlePaymentChange = (event) => {
    this.setProvider(event)

    if (event.complete) {
      this.setPayButtonActive()

      if (!this.paymentChangeEvent) {
        this.paymentChangeEvent = true

        this.purchaseButtonTarget.addEventListener('click', (event) => {
          event.preventDefault()
          this.submitPayment()
          this.setPayButtonInactive()
        })
      }
    }
    else {
      this.setPayButtonInactive(false)
    }
    this.updatePayButtonText(event)
  }

  // Submits the payment to Stripe. Aborts any in-flight address/shipping updates
  // first to prevent them from racing with the completion request. On success,
  // calls completeOrder to finalize. On error, records the error to the order.
  // For redirect-based methods (3DS, Klarna), Stripe handles the redirect via return_url.
  async submitPayment() {
    const { error: submitError } = await this.stripeElements.submit()
    if (submitError) {
      await this.handlePaymentError({ error: submitError })
      return
    }

    if (this.pendingUpdateRequest) {
      try { this.pendingUpdateRequest.abort() }
      catch { /* noop */ }
    }

    const pay = await this.stripe.confirmPayment({
      elements: this.stripeElements,
      clientSecret: this.clientSecretValue,
      confirmParams: {
        return_url: this.stripeRedirectUrlValue,
      },
      redirect: 'if_required',
    })

    if (pay.error) {
      this.handlePaymentError(pay)
    }
    else {
      await this.completeOrder(pay.paymentIntent.payment_method)
    }
  }

  // Sends the completion request to the server with retry logic (up to 3 retries
  // with linear backoff). This is critical — the payment has already been captured
  // by Stripe at this point, so we must persist the completion. Falls back to
  // page reload if all retries fail so the user isn't stuck on a dead page.
  async completeOrder(transactionId) {
    const params = {
      order: {
        status: 'complete',
        provider: this.providerValue,
        transaction_id: transactionId,
      },
    }

    const maxRetries = 3
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await put(this.updateUrlValue, {
          body: JSON.stringify(params),
          responseKind: 'json',
        })

        if (response.ok) {
          const json = await response.json
          window.location.href = json.url
          return
        }
      }
      catch { /* retry */ }

      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)))
      }
    }

    location.reload()
  }

  // Throttles server updates to at most once per second to prevent spamming the
  // server and causing race conditions. Sends progress as soon as email is valid,
  // rather than waiting for the full address too - so the order keeps getting
  // saved incrementally as the customer fills in name/address, and they can
  // leave and come back to find their progress intact. validValue (both email
  // and address complete) still gates payment readiness, just not server saves.
  checkValid = () => {
    this.validValue = this.emailValidValue && this.addressValidValue
    this.paramsValue.stripe_address_valid = this.validValue

    if (this.throttler) return
    if (!this.emailValidValue) return

    this.throttler = setTimeout(() => {
      this.addCartTotalsSkeleton()
      this.updateOrder()

      this.throttler = undefined
    }, 1000)
  }

  // Flattens Stripe's nested address structure into top-level params
  // and runs AddressFixer for any provider-specific corrections.
  updateParams = (event) => {
    Object.assign(this.paramsValue, event.value.address, event.value)
    delete this.paramsValue.address

    const addressFixer = new AddressFixer(this.paramsValue)
    this.paramsValue = addressFixer.run()
  }

  // Sends a flash message to the UI via turbo-stream (e.g. validation errors).
  updateFlash = (params) => {
    post(ORDERS_FLASH_URL, {
      body: JSON.stringify(params),
      responseKind: 'turbo-stream',
    })
  }

  // Sends the current order params to the server via turbo-stream.
  // Stores the request promise so it can be aborted before payment submission.
  updateOrder = () => {
    const params = { order: this.paramsValue }

    this.pendingUpdateRequest = put(this.updateUrlValue, {
      body: JSON.stringify(params),
      responseKind: 'turbo-stream',
    })
  }

  // Records the payment error to the order on the server, shows a flash message,
  // and remounts the payment element so the customer can retry.
  handlePaymentError = async (event) => {
    const errorMessage = event.error?.message || 'Payment failed'
    const params = {
      order: {
        status: 'error',
        checkout_errors: [errorMessage],
      },
    }

    try {
      await put(this.updateUrlValue, {
        body: JSON.stringify(params),
        responseKind: 'turbo-stream',
      })
    }
    catch { /* noop */ }

    this.updateFlash({ error: errorMessage })
    this.paymentElement.unmount()
    this.paymentElement.mount(this.paymentTarget)
    this.setPayButtonActive()
  }

  // Disables the purchase button and adds loading state.
  setPayButtonInactive = (loading = true) => {
    this.purchaseButtonTarget.disabled = true
    this.purchaseButtonTarget.classList.add('opacity-40')
    this.purchaseButtonTarget.style.cursor = 'not-allowed'

    if (loading) this.purchaseButtonTarget.classList.add('loading')
  }

  // Re-enables the purchase button and removes loading state.
  setPayButtonActive = () => {
    this.purchaseButtonTarget.disabled = false
    this.purchaseButtonTarget.classList.remove('opacity-40')
    this.purchaseButtonTarget.classList.remove('loading')
    this.purchaseButtonTarget.style.cursor = 'pointer'
  }

  // Updates button text to reflect the selected payment method
  // (e.g. "Pay with Card", "Pay with Apple Pay", "Pay with Google Pay").
  updatePayButtonText = (event) => {
    if (event.value.type) {
      const capitalize = s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
      const payment = event.value.type.split('_').map(str => capitalize(str)).join(' ')
      const buttonText = this.purchaseButtonTarget.querySelector('span')
      buttonText.innerHTML = `Pay with ${payment}`
    }
  }

  // Sets the provider value based on the payment method type.
  // Card payments use 'stripe', wallet payments use their type (apple_pay, google_pay).
  setProvider = (event) => {
    if (!event.value.type) return

    if (event.value.type === 'card') {
      this.providerValue = 'stripe'
    }
    else {
      this.providerValue = event.value.type
    }

    this.paramsValue.provider = this.providerValue
  }

  // Adds a loading skeleton to cart totals while shipping/pricing recalculates.
  addCartTotalsSkeleton = () => {
    const totals = document.querySelectorAll('.cart-totals')

    totals.forEach((element) => {
      element.classList.add('reloading')
    })
  }

  // Unmounts all Stripe elements and disables shipping.
  // Called when PayPal is selected to prevent background updates on mobile Safari.
  unmountElements = () => {
    this.addressElement.unmount()
    this.emailElement.unmount()
    this.paymentElement.unmount()
    this.shippingTarget.disabled = true
  }

  // Returns default email config for Link Authentication if a saved email exists.
  get emailOptions() {
    if (this.emailValue) {
      return { defaultValues: { email: this.emailValue } }
    }
    else {
      return {}
    }
  }

  // Payment element config: tabbed layout with Apple Pay and Google Pay enabled.
  get paymentOptions() {
    const paymentObject = {
      layout: {
        type: 'tabs',
        defaultCollapsed: false,
      },
      wallets: {
        applePay: 'auto',
        googlePay: 'auto',
      },
    }

    return paymentObject
  }

  // Address element config: shipping mode with Google Maps autocomplete,
  // PO Box blocking, and optional phone field.
  get addressOptions() {
    const optionsObject = {
      mode: 'shipping',
      autocomplete: {
        mode: 'google_maps_api',
        apiKey: this.googleMapsApiKeyValue,
      },
      blockPoBox: false,
      fields: {
        phone: 'always',
      },
      validation: {
        phone: {
          required: 'never',
        },
      },
    }
    if (this.hasCurrentAddressValue) {
      optionsObject.defaultValues = this.currentAddressValue
    }

    return optionsObject
  }

  get appearance() {
    const appearanceObject = { theme: 'flat' }

    return appearanceObject
  }

  // Reads the current order total from the DOM for Stripe amount calculations.
  get totalValue() {
    const totalElement = document.querySelector('#total-price')

    if (totalElement) {
      const stripCurrencyFormat = totalElement.innerHTML.replace('$', '').replace('.', '')
      return Number(stripCurrencyFormat)
    }
    else {
      return 0
    }
  }
}
